import { useEffect, useMemo, useRef, useState } from 'react';

const PANEL_HEIGHT = 300;

export default function SpecialitySubspecialitySelect({
    label = 'Seleccione Especialidad',
    specialities = [],
    selectedSpecialities = [],
    selectedSubSpecialities = [],
    onChange
}) {
    const containerRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [activeSpecialityId, setActiveSpecialityId] = useState(null);

    const selectedSpecialityIds = useMemo(
        () => new Set((selectedSpecialities || []).map((item) => item.value)),
        [selectedSpecialities]
    );
    const selectedSubIds = useMemo(
        () => new Set((selectedSubSpecialities || []).map((item) => item.value)),
        [selectedSubSpecialities]
    );

    const totalSelectedCount = (selectedSpecialities?.length || 0) + (selectedSubSpecialities?.length || 0);
    const selectedPreviewText = useMemo(() => {
        const selectedLabels = [...(selectedSpecialities || []), ...(selectedSubSpecialities || [])]
            .map((item) => item.label)
            .filter(Boolean);

        if (selectedLabels.length === 0) {
            return 'Seleccione...';
        }

        const firstTwo = selectedLabels.slice(0, 2).join(', ');
        const remaining = selectedLabels.length - 2;
        return remaining > 0 ? `${firstTwo} +${remaining}` : firstTwo;
    }, [selectedSpecialities, selectedSubSpecialities]);

    useEffect(() => {
        const onOutsideClick = (event) => {
            if (!containerRef.current?.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', onOutsideClick);
        return () => document.removeEventListener('mousedown', onOutsideClick);
    }, []);

    const normalizedSpecialities = useMemo(
        () =>
            (specialities || []).map((item) => ({
                ...item,
                subSpecialities: item.subSpecialities || item.secondarySpecialties || []
            })),
        [specialities]
    );

    const filteredSpecialities = useMemo(() => {
        const text = searchText.trim().toLowerCase();
        if (!text) return normalizedSpecialities;

        return normalizedSpecialities.filter((item) => {
            const inSpeciality = item.label.toLowerCase().includes(text);
            const inSub = item.subSpecialities.some((sub) => sub.name.toLowerCase().includes(text));
            return inSpeciality || inSub;
        });
    }, [normalizedSpecialities, searchText]);

    const activeSpeciality = filteredSpecialities.find((item) => item.value === activeSpecialityId) || null;
    const shouldShowSubPanel = Boolean(activeSpeciality?.subSpecialities?.length);

    const toggleDirectSpeciality = (speciality) => {
        const isSelected = selectedSpecialityIds.has(speciality.value);
        const next = isSelected
            ? selectedSpecialities.filter((item) => item.value !== speciality.value)
            : [...selectedSpecialities, { value: speciality.value, label: speciality.label }];

        onChange({
            selectedSpecialities: next,
            selectedSubSpecialities
        });
    };

    const toggleSubSpeciality = (speciality, sub) => {
        const isSelected = selectedSubIds.has(sub.id);
        const next = isSelected
            ? selectedSubSpecialities.filter((item) => item.value !== sub.id)
            : [
                  ...selectedSubSpecialities,
                  {
                      value: sub.id,
                      label: sub.name,
                      specialtyId: speciality.value,
                      specialtyLabel: speciality.label
                  }
              ];

        onChange({
            selectedSpecialities,
            selectedSubSpecialities: next
        });
    };

    const clearAll = (event) => {
        event.stopPropagation();
        onChange({
            selectedSpecialities: [],
            selectedSubSpecialities: []
        });
        setActiveSpecialityId(null);
        setSearchText('');
    };

    const onSpecialityClick = (speciality) => {
        const hasSubs = speciality.subSpecialities.length > 0;

        if (hasSubs) {
            // Solo se eligen sub-especialidades; el padre solo abre el panel.
            if (selectedSpecialityIds.has(speciality.value)) {
                onChange({
                    selectedSpecialities: selectedSpecialities.filter((item) => item.value !== speciality.value),
                    selectedSubSpecialities
                });
            }
            setActiveSpecialityId(speciality.value);
            return;
        }

        toggleDirectSpeciality(speciality);
        setActiveSpecialityId(null);
    };

    return (
        <div ref={containerRef} className="relative w-full">
            <label className="block mb-1 font-medium">{label}</label>
            <button
                type="button"
                onClick={() => setIsOpen((prev) => !prev)}
                className="w-full rounded-full border border-gray-300 bg-white px-3 py-2 text-left shadow-sm focus:outline-none focus:ring-1 focus:ring-dl-primary-500"
            >
                <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm text-gray-700">
                        {selectedPreviewText}
                    </span>
                    <div className="flex items-center gap-2">
                        {totalSelectedCount > 0 && (
                            <span
                                role="button"
                                tabIndex={0}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setIsOpen(true);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        setIsOpen(true);
                                    }
                                }}
                                className="rounded-full bg-dl-primary-700 px-2 py-0.5 text-xs text-white"
                            >
                                {totalSelectedCount}
                            </span>
                        )}
                        <span className="text-xs text-gray-500">{isOpen ? '▲' : '▼'}</span>
                    </div>
                </div>
            </button>

            {isOpen && (
                <div
                    className="absolute left-1/2 z-[9999] mt-4 w-[calc(100vw-1.5rem)] max-w-[640px] -translate-x-1/2 rounded-xl border border-gray-200 bg-white shadow-xl md:left-0 md:w-[min(95vw,640px)] md:translate-x-0"
                    style={{ minHeight: PANEL_HEIGHT }}
                >
                    <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                        <input
                            type="text"
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                            className="w-full rounded-md border border-gray-200 px-2 py-1 text-sm focus:border-dl-primary-500 focus:outline-none"
                            placeholder="Buscar especialidad..."
                        />
                        <button
                            type="button"
                            onClick={clearAll}
                            className="ml-2 shrink-0 text-xs font-medium text-dl-primary-700"
                        >
                            Clear all
                        </button>
                    </div>

                    <div
                        className={`grid ${shouldShowSubPanel ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}
                        style={{ minHeight: PANEL_HEIGHT - 48 }}
                    >
                        <div className="border-r border-gray-100">
                            <div className="max-h-[252px] overflow-y-auto">
                                {filteredSpecialities.map((speciality) => {
                                    const hasSubs = speciality.subSpecialities.length > 0;
                                    const isDirectSelected = selectedSpecialityIds.has(speciality.value);
                                    const subSelectedCount = speciality.subSpecialities.filter((sub) =>
                                        selectedSubIds.has(sub.id)
                                    ).length;

                                    return (
                                        <button
                                            type="button"
                                            key={speciality.value}
                                            onClick={() => onSpecialityClick(speciality)}
                                            className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                                                activeSpecialityId === speciality.value
                                                    ? 'bg-blue-50 text-dl-primary-700'
                                                    : 'hover:bg-gray-50'
                                            }`}
                                        >
                                            <span className="truncate">{speciality.label}</span>
                                            <div className="ml-2 flex items-center gap-2">
                                                {(isDirectSelected || subSelectedCount > 0) && (
                                                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-dl-primary-700">
                                                        {isDirectSelected ? '✓' : subSelectedCount}
                                                    </span>
                                                )}
                                                {hasSubs ? <span className="text-xs text-gray-400">›</span> : null}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {shouldShowSubPanel && (
                            <div className="relative border-l border-gray-100">
                                <div className="max-h-[252px] overflow-y-auto p-2 transition-opacity duration-200 opacity-100">
                                    {activeSpeciality.subSpecialities.map((sub) => {
                                        const checked = selectedSubIds.has(sub.id);
                                        return (
                                            <label
                                                key={sub.id}
                                                className={`mb-1 flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm ${
                                                    checked ? 'bg-blue-50 text-dl-primary-700' : 'hover:bg-gray-50'
                                                }`}
                                            >
                                                <span
                                                    className={`relative flex h-5 w-5 items-center justify-center rounded-md border transition-colors ${
                                                        checked
                                                            ? 'border-dl-primary-700 bg-dl-primary-700'
                                                            : 'border-gray-300 bg-white'
                                                    }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => toggleSubSpeciality(activeSpeciality, sub)}
                                                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                                    />
                                                    {checked && (
                                                        <svg
                                                            className="h-3 w-3 text-white"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                            aria-hidden="true"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M16.704 5.29a1 1 0 010 1.42l-7.09 7.09a1 1 0 01-1.42 0L3.296 8.9a1 1 0 011.415-1.42l4.193 4.192 6.383-6.382a1 1 0 011.417 0z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    )}
                                                </span>
                                                <span className="truncate">{sub.name}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
